import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, CheckCircle2, XCircle, Loader2, ArrowRight, AlertTriangle, User, Phone, BadgeCheck, FileText, ArrowLeft, Home } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function KYCStart() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("session");

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Form states
  const [pan, setPan] = useState("");
  const [panValidating, setPanValidating] = useState(false);
  const [panResult, setPanResult] = useState(null);
  
  // Error state for showing error screen
  const [verificationError, setVerificationError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid session. No token provided.");
      setLoading(false);
      return;
    }
    verifySession();
  }, [token]);

  const verifySession = async () => {
    try {
      const response = await axios.post(`${API}/kyc/session/verify-token?token=${token}`);
      setSession(response.data);
      
      // Check current verification status
      if (response.data.pan_verified) {
        setCurrentStep(2); // Completed
        setPanResult({ match_status: "matched" });
      }
    } catch (err) {
      // Handle error - ensure we extract string message
      let errorMsg = "Session verification failed";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || d.message || "Validation error").join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || detail.message || "Validation error";
        }
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const validatePAN = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(pan)) {
      toast.error("Invalid PAN format. Expected: XXXXX0000X");
      return;
    }

    setPanValidating(true);
    setVerificationError(null);
    
    try {
      const response = await axios.post(`${API}/kyc/pan/validate`, {
        kyc_session_id: session.session_id,
        pan: pan.toUpperCase()
      });
      
      setPanResult(response.data);
      
      if (response.data.match_status === "matched") {
        toast.success("PAN verified successfully! KYC Complete.");
        
        // Mark session as completed (PAN-only verification)
        await axios.post(`${API}/kyc/complete-pan-only`, {
          kyc_session_id: session.session_id
        });
        
        setCurrentStep(2);
      } else {
        // Show error screen with mismatch details
        setVerificationError({
          type: "name_mismatch",
          title: "Name Mismatch Detected",
          message: "The name on your PAN does not match your registered name. Please verify your details and try again.",
          details: {
            pan_name: response.data.pan_name,
            registered_name: session?.name
          }
        });
      }
    } catch (err) {
      // Show error screen with API error
      const errorMessage = err.response?.data?.detail || "PAN validation failed. Please try again.";
      setVerificationError({
        type: "api_error",
        title: "Verification Failed",
        message: typeof errorMessage === 'string' ? errorMessage : "An error occurred during verification. Please try again.",
        details: null
      });
    } finally {
      setPanValidating(false);
    }
  };

  const handleBackToMain = () => {
    // Get UID from session if available
    const uid = session?.taj_user_id;
    if (uid) {
      navigate(`/?uid=${uid}`);
    } else {
      navigate("/");
    }
  };

  const handleRetry = () => {
    setVerificationError(null);
    setPan("");
    setPanResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Verifying session...</p>
        </div>
      </div>
    );
  }

  // Session error screen
  if (error) {
    // Try to get UID from session or URL for proper back navigation
    const backUid = session?.taj_user_id;
    const backUrl = backUid ? `/?uid=${backUid}` : "/";
    
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary text-primary-foreground py-4 px-4">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
              alt="Taj Cement" 
              className="h-8 w-auto brightness-0 invert"
            />
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>PAN VERIFICATION</h1>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="rounded-sm" data-testid="kyc-error-card">
            <CardContent className="pt-8 pb-8 text-center space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              
              <div>
                <h2 className="text-xl font-bold text-destructive" style={{ fontFamily: 'Manrope' }}>Session Error</h2>
                <p className="text-muted-foreground mt-2">{typeof error === 'string' ? error : 'Session expired or invalid'}</p>
              </div>

              <Button 
                className="rounded-none"
                onClick={() => navigate(backUrl)}
                data-testid="kyc-back-home-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </main>
        
        <footer className="border-t py-4 px-4 mt-8">
          <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
            <p className="text-xs text-muted-foreground">Powered by</p>
            <img 
              src={require("@/assets/tagnpay-logo.png")} 
              alt="Tag'N'Pay" 
              className="h-6 w-auto"
            />
          </div>
        </footer>
      </div>
    );
  }

  // Verification error screen (name mismatch or API error)
  if (verificationError) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary text-primary-foreground py-4 px-4">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
              alt="Taj Cement" 
              className="h-8 w-auto brightness-0 invert"
            />
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>PAN VERIFICATION</h1>
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto px-4 py-16">
          <Card className="rounded-sm" data-testid="kyc-verification-error-card">
            <CardContent className="pt-8 pb-8 space-y-6">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
                  {verificationError.type === "name_mismatch" ? (
                    <AlertTriangle className="w-10 h-10 text-destructive" />
                  ) : (
                    <XCircle className="w-10 h-10 text-destructive" />
                  )}
                </div>
                
                <h2 className="text-xl font-bold text-destructive mt-4" style={{ fontFamily: 'Manrope' }}>
                  {verificationError.title}
                </h2>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                  {verificationError.message}
                </p>
              </div>

              {/* Show mismatch details if available */}
              {verificationError.details && (
                <div className="bg-destructive/5 border border-destructive/20 rounded-sm p-4">
                  <h3 className="font-semibold text-destructive mb-3">Details:</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name on PAN:</span>
                      <span className="font-medium">{verificationError.details.pan_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Registered Name:</span>
                      <span className="font-medium">{verificationError.details.registered_name}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  variant="outline"
                  className="flex-1 h-12 rounded-none"
                  onClick={handleBackToMain}
                  data-testid="kyc-error-back-btn"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Back to Main
                </Button>
                <Button 
                  className="flex-1 h-12 rounded-none btn-gold"
                  onClick={handleRetry}
                  data-testid="kyc-error-retry-btn"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Try Again
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                If you believe this is an error, please contact support with your registered mobile number.
              </p>
            </CardContent>
          </Card>
        </main>
        
        <footer className="border-t py-4 px-4 mt-8">
          <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
            <p className="text-xs text-muted-foreground">Powered by</p>
            <img 
              src={require("@/assets/tagnpay-logo.png")} 
              alt="Tag'N'Pay" 
              className="h-6 w-auto"
            />
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" 
              alt="Taj Cement" 
              className="h-8 w-auto brightness-0 invert"
            />
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>PAN VERIFICATION</h1>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-primary-foreground hover:bg-primary-foreground/10"
            onClick={handleBackToMain}
            data-testid="kyc-header-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-muted py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-8">
            {[
              { num: 1, label: "PAN Verification", icon: FileText },
              { num: 2, label: "Complete", icon: CheckCircle2 }
            ].map((step, index) => (
              <div key={step.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`step-indicator ${
                      currentStep > step.num ? "completed" : 
                      currentStep === step.num ? "active" : "pending"
                    }`}
                    data-testid={`step-indicator-${step.num}`}
                  >
                    {currentStep > step.num ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-xs mt-2 font-medium">{step.label}</span>
                </div>
                {index < 1 && (
                  <div className={`w-24 md:w-32 h-0.5 mx-4 ${currentStep > step.num ? "bg-primary" : "bg-border"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* User Info Card */}
        <Card className="rounded-sm mb-6" data-testid="kyc-user-info-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <User className="w-5 h-5" />
              User Information
            </CardTitle>
            <CardDescription>Provided by Taj Cement</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="form-field">
                <Label className="form-label">Name</Label>
                <div className="readonly-field" data-testid="kyc-user-name">
                  {session?.name}
                </div>
              </div>
              <div className="form-field">
                <Label className="form-label">Mobile</Label>
                <div className="readonly-field" data-testid="kyc-user-mobile">
                  <Phone className="w-4 h-4 mr-2 text-muted-foreground" />
                  {session?.mobile}
                </div>
              </div>
              <div className="form-field">
                <Label className="form-label">User Type</Label>
                <div className="readonly-field capitalize" data-testid="kyc-user-type">
                  {session?.user_type?.replace("_", " ")}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: PAN Verification */}
        {currentStep === 1 && (
          <Card className="rounded-sm fade-in" data-testid="kyc-pan-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileText className="w-5 h-5" />
                PAN Verification
              </CardTitle>
              <CardDescription>
                Enter your PAN to verify your identity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="form-field">
                <Label className="form-label">PAN Number</Label>
                <Input
                  className="input-industrial uppercase text-lg tracking-wider"
                  placeholder="XXXXX0000X"
                  value={pan}
                  onChange={(e) => setPan(e.target.value.toUpperCase())}
                  maxLength={10}
                  data-testid="kyc-pan-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Format: 5 letters + 4 digits + 1 letter
                </p>
              </div>

              <Button
                className="w-full h-14 rounded-none uppercase tracking-wider btn-gold"
                onClick={validatePAN}
                disabled={panValidating || pan.length !== 10}
                data-testid="kyc-pan-verify-btn"
              >
                {panValidating ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Shield className="w-5 h-5 mr-2" />
                )}
                Verify PAN
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Completed */}
        {currentStep === 2 && (
          <Card className="rounded-sm fade-in" data-testid="kyc-complete-step">
            <CardContent className="pt-8 pb-8 text-center space-y-6">
              <div 
                className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #F2B721 0%, #F9D96A 50%, #F2B721 100%)' }}
              >
                <CheckCircle2 className="w-12 h-12 text-primary" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Manrope' }}>
                  KYC Verification Complete!
                </h2>
                <p className="text-muted-foreground mt-2">
                  Your PAN has been successfully verified.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <BadgeCheck className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Verified Details</span>
                </div>
                <div className="text-sm text-green-700 space-y-1">
                  <p><strong>Name:</strong> {panResult?.pan_name || session?.name}</p>
                  <p><strong>PAN:</strong> {pan ? `${pan.slice(0,2)}****${pan.slice(-2)}` : "****"}</p>
                </div>
              </div>

              <Button 
                className="rounded-none btn-gold"
                onClick={handleBackToMain}
                data-testid="kyc-done-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Main
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t py-4 px-4 mt-8">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-3">
          <p className="text-xs text-muted-foreground">Powered by</p>
          <img 
            src={require("@/assets/tagnpay-logo.png")} 
            alt="Tag'N'Pay" 
            className="h-6 w-auto"
          />
        </div>
      </footer>
    </div>
  );
}
